import { get, isEmpty } from 'lodash';
import { shouldRemoveOptionalMemberIdCopySelector } from 'src/config/ab/selector';
import { INSURANCE_ID_REQUIRED } from '../../components/AppointmentRequirements/constants';
import { hasRequirement } from '../../components/AppointmentRequirements/helpers';
import {
    SCHEDULING_ROOT_KEY,
    DOCTOR_CARD_ROOT_KEY,
    PATIENT_INSURANCE_TYPES,
    BOOKING_CONTAINER_ROOT_KEY,
    APPOINTMENT_DETAILS_ROOT_KEY,
    BookingStateProperties,
} from '../../constants';
import { DENTAL_INSURANCE_SPECIALTIES } from '../../constants';
import bookingStateSelector from '../../selectors/BookingStateSelector';
import insuranceNotTakenRequirementSelector from '../../selectors/InsuranceNotTakenRequirementSelector';
import patientInsuranceSelector from '../../selectors/PatientInsuranceSelector';
import patientSelector from '../../selectors/PatientSelector';
import { getDoctorCardProps } from '../../utils';
import appointmentDetailsSelector from './AppointmentDetailsSelector';

// used to determine whether or not we can start polling for the booking state status
const inBetweenUpdateAndSubmit = (state) => {
    const {
        submitBookingStateFailure,
        submitBookingSucceeded,
        submittingBookingState,
        updateBookingStateSuccess,
    } = state[SCHEDULING_ROOT_KEY];
    return (
        !submittingBookingState &&
        !submitBookingSucceeded &&
        !submitBookingStateFailure &&
        updateBookingStateSuccess
    );
};

export const mapStateToProps = (state = {}) => {
    if (state[APPOINTMENT_DETAILS_ROOT_KEY] === undefined) {
        throw new Error(
            'Expected the appointment details page state to be available as `state.appointmentDetailsPage`. ' +
                "Ensure you have added the `appointmentDetailsReducer` to your store's reducers via `combineReducers`."
        );
    }

    const bookingState = bookingStateSelector(state);
    const bookingStatus = get(bookingState, 'status');

    // Loading mode
    const disableContinue = get(
        state[APPOINTMENT_DETAILS_ROOT_KEY],
        'disableContinue',
        false
    );
    const { submittingBookingState, submitBookingStateFailure } =
        state[SCHEDULING_ROOT_KEY];

    // Patient info
    const patient = patientSelector(state);
    const { patientId } = patient;
    const patientInsurance =
        patientId && get(patient, 'insurance')
            ? patientInsuranceSelector(patient)
            : undefined;

    // Details Mode
    const referralId = get(bookingState, BookingStateProperties.referralId);
    const requirements = get(
        bookingState,
        BookingStateProperties.requirements,
        []
    );
    const insuranceIdRequired = hasRequirement(
        INSURANCE_ID_REQUIRED,
        requirements
    );
    const insuranceId = get(
        state[APPOINTMENT_DETAILS_ROOT_KEY],
        'insuranceId',
        ''
    );
    const insuranceIdSourceType = get(
        state[APPOINTMENT_DETAILS_ROOT_KEY],
        'insuranceIdSourceType',
        ''
    );
    const { insuranceError } = state[APPOINTMENT_DETAILS_ROOT_KEY];
    const firstName = get(
        state[DOCTOR_CARD_ROOT_KEY],
        'professional.firstName',
        ''
    );
    const lastName = get(
        state[DOCTOR_CARD_ROOT_KEY],
        'professional.lastName',
        ''
    );
    const insuranceCarrierName = get(
        state[APPOINTMENT_DETAILS_ROOT_KEY],
        'insuranceCarrier'
    );
    const insurancePlanName = get(
        state[APPOINTMENT_DETAILS_ROOT_KEY],
        'insurancePlan'
    );
    const specialtyId = get(
        state[DOCTOR_CARD_ROOT_KEY],
        'professional.specialtyId'
    );
    const insuranceName = `${insuranceCarrierName} - ${insurancePlanName}`;
    let professionalName = `${firstName} ${lastName}`;
    const title = get(state.doctorCard, 'professional.title');
    if (title) {
        professionalName += `, ${title}`;
    }

    let insuranceTypeToUse = PATIENT_INSURANCE_TYPES.HEALTH;

    if (DENTAL_INSURANCE_SPECIALTIES.has(specialtyId)) {
        insuranceTypeToUse = PATIENT_INSURANCE_TYPES.DENTAL;
    }

    const insuranceCarrierId = get(bookingState, 'insurance_carrier_id');
    const insurancePlanId = get(bookingState, 'insurance_plan_id');

    const appointmentDetails = appointmentDetailsSelector(state);

    // Only show a field for insurance member id if the user has an
    // insurance (not during I'M PAYING FOR MYSELF)
    const shouldShowInsuranceMemberIdInput =
        insuranceIdRequired ||
        (parseInt(insuranceCarrierId, 10) > 0 &&
            parseInt(insurancePlanId, 10) > 0);

    const shouldShowOptionalMemberIdCopy =
        !shouldRemoveOptionalMemberIdCopySelector(state);

    return {
        ...appointmentDetails,
        phoneNumber: get(state.phone, 'phoneNumber', ''),
        bookingState,
        bookingStatus,

        // Loading mode
        submittingBookingState,
        submitBookingStateFailure,
        inBetweenUpdateAndSubmit: inBetweenUpdateAndSubmit(state),
        disableContinue, // for inital insurance info fetch

        // Details mode
        referralId,
        professionalName,
        specialtyId,
        insuranceName,
        insuranceCarrierName,
        insurancePlanName,
        insuranceId,
        insuranceIdSourceType,

        // Patient info
        patientInsurance,
        insuranceTypeToUse,

        bookAppointmentEnabled:
            get(state.appointmentRequirements, 'isValid', true) &&
            (insuranceIdRequired ? !isEmpty(insuranceId.trim()) : true),
        insuranceIdRequired,
        insuranceError,
        bookingErrors: state[SCHEDULING_ROOT_KEY].bookingErrors,
        bookingErrorCode: state[SCHEDULING_ROOT_KEY].bookingErrorCode,
        localInSync: state[SCHEDULING_ROOT_KEY].localInSync,
        insuranceNotTakenRequirement:
            insuranceNotTakenRequirementSelector(state),
        shouldShowInsuranceMemberIdInput,
        shouldRenderDoctorNotes: !(bookingState && bookingState.referral_id),
        modalIsShowing: state[BOOKING_CONTAINER_ROOT_KEY].showModal,
        ...getDoctorCardProps(state),

        shouldShowOptionalMemberIdCopy,
    };
};

export default mapStateToProps;
